import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  Loader2, 
  Mail, 
  User, 
  Calendar, 
  Clock,
  ShieldAlert
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAdminKey = () => sessionStorage.getItem('admin_key');

const AdminUserCard = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    const adminKey = getAdminKey();
    if (!adminKey) {
      setError('Требуется авторизация');
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        headers: {
          'X-ADMIN-KEY': adminKey
        }
      });
      
      if (response.status === 401 || response.status === 403) {
        setError('Доступ запрещён');
        return;
      }
      
      if (!response.ok) throw new Error('Пользователь не найден');
      
      const data = await response.json();
      setUserData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Активен</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Ожидает подтверждения</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <ShieldAlert className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Ошибка</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/admin')}>
            Назад к списку
          </Button>
        </div>
      </div>
    );
  }

  if (!userData) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Назад
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Карточка пользователя</h1>
          <p className="text-gray-500">{userData.email}</p>
        </div>
        <Badge variant="outline" className="text-xs">
          READ-ONLY
        </Badge>
      </div>

      {/* User Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Информация о пользователе
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-500">ID</label>
              <p className="font-mono text-sm">{userData.id}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500">Статус</label>
              <div className="mt-1">{getStatusBadge(userData.status)}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-gray-600">
            <Mail className="h-4 w-4" />
            <span>{userData.email}</span>
          </div>
          
          <div className="flex items-center gap-2 text-gray-600">
            <User className="h-4 w-4" />
            <span>{userData.name || '—'}</span>
          </div>
          
          <div className="flex items-center gap-2 text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Регистрация: {formatDate(userData.created_at)}</span>
          </div>
          
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="h-4 w-4" />
            <span>Последний вход: {formatDate(userData.last_login)}</span>
          </div>
        </CardContent>
      </Card>
      
      {/* Read-only notice */}
      <div className="text-center text-sm text-gray-500">
        Это режим только для чтения. Редактирование недоступно.
      </div>
    </div>
  );
};

export default AdminUserCard;
